import React, { useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Stack,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Divider,
  Alert,
} from '@mui/material';
import { Calendar, CheckCircle, XCircle, Clock, User } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { leaveService } from '@/services/api';
import { LeaveRequest, LeaveStatus, LeaveType } from '@/types';
import { useAppSelector } from '@/store';
import { selectCurrentUser } from '@/store/slices/authSlice';
import dayjs from 'dayjs';
import { QUERY_KEYS } from '@/services/queryClient';

const LeaveApprovalsView: React.FC = () => {
  const currentUser = useAppSelector(selectCurrentUser);
  const queryClient = useQueryClient();
  const [selectedLeave, setSelectedLeave] = useState<LeaveRequest | null>(null);
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);
  const [comments, setComments] = useState('');

  const { data: pendingLeavesRaw, isLoading } = useQuery({
    queryKey: [...QUERY_KEYS.pendingApprovals, 'leaves'],
    queryFn: () => leaveService.getPendingApprovals(),
    enabled: !!currentUser,
  });
  const pendingLeaves: LeaveRequest[] = Array.isArray(pendingLeavesRaw?.data)
    ? pendingLeavesRaw.data
    : [];

  const approveMutation = useMutation({
    mutationFn: ({ leaveId, comments }: { leaveId: string; comments?: string }) =>
      leaveService.approveLeave(leaveId, comments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingApprovals });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
      handleCloseDialog();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: ({ leaveId, comments }: { leaveId: string; comments: string }) =>
      leaveService.rejectLeave(leaveId, comments ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pendingApprovals });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.myLeaves });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.calendarEvents });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.leaveBalance });
      handleCloseDialog();
    },
  });

  const handleOpenDialog = (leave: LeaveRequest, type: 'approve' | 'reject') => {
    setSelectedLeave(leave);
    setActionType(type);
    setComments('');
  };

  const handleCloseDialog = () => {
    setSelectedLeave(null);
    setActionType(null);
    setComments('');
  };

  const handleSubmitAction = () => {
    if (!selectedLeave) return;

    if (actionType === 'approve') {
      approveMutation.mutate({ leaveId: selectedLeave.id, comments });
    } else if (actionType === 'reject') {
      rejectMutation.mutate({ leaveId: selectedLeave.id, comments: comments ?? '' });
    }
  };

  const getLeaveTypeLabel = (type: LeaveType) => {
    return type.replace('_', ' ');
  };

  const getLeaveTypeColor = (type: LeaveType) => {
    const colors = {
      [LeaveType.CASUAL]: '#3b82f6',
      [LeaveType.SICK]: '#ef4444',
      [LeaveType.EARNED]: '#10b981',
      [LeaveType.UNPAID]: '#94a3b8',
      [LeaveType.COMPENSATORY]: '#8b5cf6',
    };
    return colors[type] || '#64748b';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Calendar size={24} />
        <Typography variant="h5" fontWeight={600}>
          Leave Approvals
        </Typography>
        {pendingLeaves.length > 0 && (
          <Chip
            label={`${pendingLeaves.length} pending`}
            color="warning"
            size="small"
          />
        )}
      </Box>

      {isLoading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">Loading...</Typography>
        </Box>
      ) : pendingLeaves.length === 0 ? (
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CheckCircle size={48} style={{ color: '#22c55e', marginBottom: 16 }} />
          <Typography variant="h6" color="text.secondary">
            No pending leave requests
          </Typography>
        </Card>
      ) : (
        <Stack spacing={2}>
          {pendingLeaves.map((leave) => (
            <Card
              key={leave.id}
              sx={{
                p: 3,
                borderRadius: 2,
                boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                '&:hover': { boxShadow: '0 4px 12px rgba(0,0,0,0.12)' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      <User size={20} />
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>
                        {leave.userName || leave.userId}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Requested {dayjs(leave.createdAt || new Date()).fromNow()}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Leave Type
                      </Typography>
                      <Chip
                        label={getLeaveTypeLabel(leave.type)}
                        sx={{
                          bgcolor: `${getLeaveTypeColor(leave.type)}20`,
                          color: getLeaveTypeColor(leave.type),
                          fontWeight: 600,
                        }}
                      />
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        Duration
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Calendar size={16} />
                        <Typography variant="body1" fontWeight={500}>
                          {dayjs(leave.startDate).format('MMM DD, YYYY')} -{' '}
                          {dayjs(leave.endDate).format('MMM DD, YYYY')}
                        </Typography>
                        <Chip
                          label={`${leave.totalDays} ${leave.totalDays === 1 ? 'day' : 'days'}`}
                          size="small"
                          color="primary"
                        />
                      </Box>
                    </Box>

                    {leave.reason && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Reason
                        </Typography>
                        <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'text.primary' }}>
                          "{leave.reason}"
                        </Typography>
                      </Box>
                    )}

                    {leave.impactAnalysis?.tasksAtRisk && leave.impactAnalysis.tasksAtRisk.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                          Affected Tasks ({leave.impactAnalysis.tasksAtRisk.length})
                        </Typography>
                        <Stack spacing={0.5}>
                          {leave.impactAnalysis.tasksAtRisk.slice(0, 5).map((task) => (
                            <Box
                              key={task.taskId}
                              sx={{
                                p: 1,
                                bgcolor: 'warning.50',
                                borderRadius: 1,
                                borderLeft: 3,
                                borderColor: task.riskLevel === 'high' ? 'error.main' : 'warning.main',
                              }}
                            >
                              <Typography variant="caption" fontWeight={600} display="block">
                                {task.taskTitle || task.taskId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Due: {dayjs(task.dueDate).format('MMM DD, YYYY')}
                              </Typography>
                            </Box>
                          ))}
                          {leave.impactAnalysis.tasksAtRisk.length > 5 && (
                            <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }}>
                              +{leave.impactAnalysis.tasksAtRisk.length - 5} more tasks
                            </Typography>
                          )}
                        </Stack>
                      </Box>
                    )}
                  </Stack>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 3 }}>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<CheckCircle size={16} />}
                    onClick={() => handleOpenDialog(leave, 'approve')}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    Approve
                  </Button>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<XCircle size={16} />}
                    onClick={() => handleOpenDialog(leave, 'reject')}
                    disabled={approveMutation.isPending || rejectMutation.isPending}
                  >
                    Reject
                  </Button>
                </Box>
              </Box>
            </Card>
          ))}
        </Stack>
      )}

      {/* Confirmation Dialog */}
      <Dialog open={!!selectedLeave && !!actionType} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {actionType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {actionType === 'approve'
              ? 'Are you sure you want to approve this leave request?'
              : 'Are you sure you want to reject this leave request? Please provide a reason.'}
          </Typography>
          {(approveMutation.isError || rejectMutation.isError) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {((approveMutation.error || rejectMutation.error) as Error)?.message || 'Action failed. Please try again.'}
            </Alert>
          )}
          <TextField
            fullWidth
            multiline
            rows={3}
            label={actionType === 'approve' ? 'Comments (Optional)' : 'Rejection Reason'}
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            placeholder={
              actionType === 'approve'
                ? 'Add any comments for the employee...'
                : 'Explain why this request is being rejected...'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={approveMutation.isPending || rejectMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmitAction}
            variant="contained"
            color={actionType === 'approve' ? 'success' : 'error'}
            disabled={approveMutation.isPending || rejectMutation.isPending}
          >
            {approveMutation.isPending || rejectMutation.isPending ? 'Processing...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LeaveApprovalsView;
